import pickBy from 'lodash/pickBy'
import mapKeys from 'lodash/mapKeys'
import memoize from 'lodash/memoize'
import axios from 'axios'
import autobind from 'autobind-decorator'
import env from 'misc/env'
import feathersSocket from 'feathers-socketio/client'
import IO from 'socket.io-client'
import feathers from 'feathers-client'

const createFeathersClient = memoize(async function createFeathersClient (token) {
  const socket = IO.connect(env.JOB_API, {
    query: `token=${token}`
  })
  return feathers()
    .configure(feathersSocket(socket))
})

@autobind
export default class TaxfyleAPI {
  constructor ({ authStore }) {
    this.authStore = authStore
    this.jobs = new JobsAPI(this)
    this.jobEvents = new JobEventsAPI(this)
    this.profiles = new ProfilesAPI(this)
    this.coupons = new CouponsAPI(this)
    this.cards = new CardsAPI(this)
    this.customers = new CustomersAPI(this)
    this.legends = new LegendsAPI(this)
    this.states = new StatesAPI(this)
    this.charges = new ChargesAPI(this)
    this.account = new AccountAPI(this)
    this.documents = new DocumentsAPI(this)
    this.sweep = new SweepAPI(this)
  }

  get apiClient () {
    return this.client(env.JOB_API)
  }

  get localClient () {
    return this.client()
  }

  client (baseUrl) {
    return axios.create({
      baseURL: baseUrl ? baseUrl + '/' : '',
      headers: {
        Authorization: `Bearer ${this.authStore.token}`
      }
    })
  }
}

class APIClient {
  constructor (api) {
    this.api = api
    this.api.authStore.onLogin(async () => {
      await createFeathersClient(this.api.authStore.token)
    })
  }

  on (eventName, handler) {
    this.api.authStore.onLogin(async () => {
      const client = await createFeathersClient(this.api.authStore.token)
      client.service(this.service).on(eventName, handler)
    })
  }
}

class JobsAPI extends APIClient {
  service = 'jobs'

  get (id) {
    return this.api.apiClient.get(`/jobs/${id}`).then(_data)
  }

  find () {
    const params = { $limit: 100 }
    return this.api.apiClient.get('/jobs', { params }).then(_data).then(x => x.data)
  }

  rate (jobId, params) {
    return this.api.apiClient.patch(`/jobs/${jobId}`, params).then(_data)
  }

  create (data) {
    return this.api.apiClient.post('/jobs', data).then(_data)
  }

  patch (id, data) {
    return this.api.apiClient.patch(`/jobs/${id}`, data).then(_data)
  }

  price (id) {
    return this.api.apiClient.get(`/job-price/${id}`).then(_data)
  }
}

class JobEventsAPI extends APIClient {
  service = 'job-events'

  find (query) {
    return this.api.apiClient.get('/job-events', { params: query }).then(_data)
  }
}

class ProfilesAPI extends APIClient {
  get (id) {
    return this.api.localClient.get(`/api/profile/${id}`).then(_data)
  }

  patch (id, data) {
    return this.api.localClient.patch(`/api/profile/${id}`, data).then(_data)
  }

  uploadPicture (file) {
    const data = new FormData()
    data.append('file', file)
    return this.api.localClient.put(`/api/profile/me/picture`, data).then(_data)
  }
}

class LegendsAPI extends APIClient {
  get client () {
    return this.api.client(env.LEGEND_API)
  }

  find () {
    return this.client.get('/legends').then(_data)
  }

  get (id) {
    return this.client.get(`/legends/${id}`).then(_data)
  }
}

class StatesAPI extends APIClient {
  get client () {
    return this.api.client(env.LEGEND_API)
  }

  find () {
    return this.client.get('/states').then(_data)
  }
}

class ChargesAPI extends APIClient {
  get client () {
    return this.api.client(env.STRIPE_API)
  }

  create (charge) {
    return this.client.post('/charges', charge).then(_data)
  }
}

class CardsAPI extends APIClient {
  get client () {
    return this.api.client(env.STRIPE_API)
  }

  find () {
    return this.client.get('/cards').then(_data).then(x => x.data)
  }

  create (card) {
    return this.client.post('/cards', card).then(_data)
  }

  destroy (id) {
    return this.client.delete(`/cards/${id}`).then(_data)
  }
}

// Stripe customers
class CustomersAPI extends APIClient {
  get client () {
    return this.api.client(env.STRIPE_API)
  }

  get (id) {
    return this.client.get(`/customers/${id}`).then(_data)
  }

  patch (id, data) {
    return this.client.patch(`/customers/${id}`, data).then(_data)
  }
}

class CouponsAPI extends APIClient {
  get client () {
    return this.api.client(env.COUPON_API)
  }

  getUserCoupons () {
    return this.client.get('/api/users/me/coupons').then(_data)
  }

  addCouponToUser (code) {
    return this.client.post('/api/users/me/coupons', { couponId: code }).then(_data)
  }

  calculateNewPrice (price) {
    return this.client.get('/api/users/me/calculation', { params: { originalPrice: price } }).then(_data)
  }
}

class AccountAPI extends APIClient {
  get client () {
    return this.api.client(env.ACCOUNT_API)
  }

  updateMeta (meta) {
    meta = pickBy(meta, x => x !== undefined)
    return this.client.patch('/users/me/referrer', meta).then(_data)
  }
}

class SweepAPI extends APIClient {
  create (data) {
    return this.api.localClient.post(`/api/sweep`, data).then(_data)
  }
}

class DocumentsAPI extends APIClient {
  get client () {
    return this.api.client(env.DOCUMENTS_API)
  }

  get (id, params) {
    return this.client.get(`/documents/${encodeURIComponent(id)}`, { params }).then(_data)
  }

  find (params) {
    return this.client.get('/documents', { params }).then(_data)
  }

  patch (id, params) {
    const { jobId, ...other } = params
    return this.client.patch(`/documents/${encodeURIComponent(id)}`, other, {
      headers: {
        'x-jobid': jobId
      }
    }).then(_data)
  }

  async upload (opts) {
    opts = {
      progress: () => {},
      file: null,
      meta: {},
      ...opts
    }

    const data = new window.FormData()
    data.append('file', opts.file)
    const cfg = {
      headers: {
        ...opts.job && ({
          'x-jobid': opts.job
        }),
        ...mapKeys(
          pickBy(opts.meta, (v) => v !== undefined && v !== null),
          (value, key) => `x-meta-${key}`
        )
      },
      onUploadProgress: (e) => {
        opts.progress({
          percent: Math.ceil((e.loaded / e.total) * 100),
          loaded: e.loaded,
          total: e.total
        })
      }
    }

    const response = await this.client.post('/documents', data, cfg)
    return response.data
  }
}

function _data (response) {
  return response.data
}



// WEBPACK FOOTER //
// ./src/client/misc/TaxfyleAPI.js